import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete,
  MatOption,
} from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import {
  partyType,
  representativeType,
} from 'src/app/components/dashboard/enums/enum-descriptors';
import {
  CountriesService,
  Country,
} from 'src/app/services/countries/countries.service';
import { EnumsService } from 'src/app/services/enums/enums.service';
import { PartyType, RepresentativeType } from 'src/types';
import {
  CompanySearchAutocompleteItem,
  CompanySearchService,
} from 'src/app/shared/company-search-service/company-search.service';
import { EditCaseDialogService } from '../edit-case-dialog.service';
import { LockRepresentativesDialogComponent } from '../lock-representatives-dialog/lock-representatives-dialog.component';
import { UnlockRepresentativesDialogComponent } from '../unlock-representatives-dialog/unlock-representatives-dialog.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { BankAccountFormComponent } from '../bank-account-form/bank-account-form.component';
import { AddressFormComponent } from '../address-form/address-form.component';
import { PayeeDatepickerComponent } from '../../../shared/payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeIconButtonComponent } from '../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { RepresentativesFormComponent } from '../representatives-form/representatives-form.component';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-client-form',
  templateUrl: './client-form.component.html',
  styleUrls: ['./client-form.component.scss'],
  imports: [
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    PayeeAutocompleteComponent,
    BankAccountFormComponent,
    AddressFormComponent,
    PayeeDatepickerComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    RepresentativesFormComponent,
    DatePipe,
  ],
})
export class ClientFormComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private editCaseDialogService = inject(EditCaseDialogService);
  private enumsService = inject(EnumsService);
  private countriesService = inject(CountriesService);
  private companySearchService = inject(CompanySearchService);

  private readonly destroy = new Subject<void>();
  private readonly companySearch$ = new Subject<string>();

  readonly loading = signal(false);
  autocompleteOptions: CompanySearchAutocompleteItem[] = [];
  partyTypes: PartyType[] = [];
  partyTypeOptions: number[] = [];
  representativeTypes: RepresentativeType[] = [];
  countries: Country[] = [];

  get formGroup() {
    return this.editCaseDialogService.form.controls.client!;
  }
  get name() {
    return this.formGroup.controls.name;
  }
  get tax_number() {
    return this.formGroup.controls.tax_number;
  }
  get bank_account() {
    return this.formGroup.controls.bank_account;
  }
  get address() {
    return this.formGroup.controls.address;
  }
  get client_data() {
    return this.formGroup.controls.client_data;
  }
  get birth_date() {
    return this.client_data.controls.birth_date;
  }
  get birth_place() {
    return this.client_data.controls.birth_place;
  }
  get mother_name() {
    return this.client_data.controls.mother_name;
  }
  get registration_number() {
    return this.client_data.controls.registration_number;
  }
  get party_type_id() {
    return this.formGroup.controls.party_type_id;
  }
  get representatives() {
    return this.formGroup.controls.representatives;
  }

  get isDuplicated(): boolean {
    return !!this.editCaseDialogService.data?.client.is_duplicated;
  }

  async ngOnInit() {
    this.loading.set(true);
    await Promise.allSettled([
      this.enumsService
        .getEnum<PartyType>(partyType.model)
        .then((v) => (this.partyTypes = v)),
      this.enumsService
        .getEnum<RepresentativeType>(representativeType.model)
        .then((v) => (this.representativeTypes = v)),
      this.countriesService
        .getCountries()
        .then((v) => (this.countries = v.countries)),
    ]);
    this.partyTypeOptions = this.partyTypes.map((pt: PartyType) => pt.id);
    this.loading.set(false);

    this.companySearch$
      .pipe(takeUntil(this.destroy), debounceTime(500))
      .subscribe(async (name) => {
        try {
          this.autocompleteOptions =
            await this.companySearchService.getCompanySearchAutocompleteItems(
              name,
            );
        } catch (error) {
          console.error('Error while searching client', error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.companySearch$.complete();
  }

  onCompanySearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.companySearch$.next(value);
  }

  async companySelected(event: MatAutocompleteSelectedEvent): Promise<void> {
    const item = event.option.value as CompanySearchAutocompleteItem;
    await this.editCaseDialogService.applyCompanySearchToParty(
      this.formGroup,
      item,
      this.registration_number,
    );
  }

  isOrg(): boolean {
    return (
      this.partyTypes.find((p) => p.id === this.formGroup.value.party_type_id)
        ?.type !== 'ind'
    );
  }

  isHouse(): boolean {
    return (
      this.partyTypes.find((p) => p.id === this.formGroup.value.party_type_id)
        ?.internal_id === '91-a'
    );
  }

  lockRepresentatives(): void {
    this.dialog.open(LockRepresentativesDialogComponent, {
      data: {
        lock_representatives: this.formGroup.controls.lock_representatives,
        representatives: this.formGroup.controls.representatives,
      },
    });
  }

  unlockRepresentatives(): void {
    this.dialog.open(UnlockRepresentativesDialogComponent, {
      data: {
        lock_representatives: this.formGroup.controls.lock_representatives,
        representatives: this.formGroup.controls.representatives,
      },
    });
  }

  readonly partyTypeDisplayWith = (id: number): string => {
    return this.partyTypes.find((pt) => pt.id == id)?.label || '';
  };
}
